import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import AppDataGrid from "../components/AppDataGrid";
import StatusNotesPanel from "../components/StatusNotesPanel";
import { BASE_URL } from "../utils";
import { buildPdfLink, formatDate, formatMoney, safeText } from "./acrisLegacyUtils";
import FamilyHistoryModal from "./FamilyHistoryModal";
import {
  FiUsers,
  FiArrowLeft,
  FiExternalLink,
  FiFileText,
  FiHome,
  FiShield,
  FiClock,
  FiMapPin,
  FiCheckCircle,
} from "react-icons/fi";

const toStr = (v) => (v == null ? "" : String(v));
const onlyDigits = (v) => toStr(v).replace(/\D+/g, "");

const getGridHeight = (rows = [], rowHeight = 42, headerHeight = 42, min = 180, max = 250) => {
  const count = Array.isArray(rows) ? rows.length : 0;
  const calculated = headerHeight + Math.max(count, 1) * rowHeight + 2;
  return Math.max(min, Math.min(max, calculated));
};

const SectionCard = ({ title, subtitle = "", icon = null, children, className = "" }) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_8px_24px_rgba(15,23,42,0.05)] ${className}`}
  >
    <div className="mb-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {icon ? (
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
              {icon}
            </span>
          ) : null}
          <div className="min-w-0">
            <div className="text-[13px] font-semibold tracking-tight text-slate-900">{title}</div>
            {subtitle ? <div className="mt-0.5 text-[11px] text-slate-500">{subtitle}</div> : null}
          </div>
        </div>
      </div>
    </div>
    {children}
  </div>
);

const GridCard = ({ title, subtitle = "", icon = null, children, className = "" }) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_8px_24px_rgba(15,23,42,0.05)] ${className}`}
  >
    <div className="mb-3 flex items-start gap-2.5">
      {icon ? (
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
          {icon}
        </span>
      ) : null}
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-slate-900">{title}</div>
        {subtitle ? <div className="mt-0.5 text-[11px] text-slate-500">{subtitle}</div> : null}
      </div>
    </div>

    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      {children}
    </div>
  </div>
);

const Field = ({ label, value, className = "" }) => {
  const content = React.isValidElement(value) ? value : safeText(value);
  return (
    <div
      className={`rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2.5 transition hover:border-slate-300 hover:bg-slate-50 ${className}`}
    >
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
        {label}
      </div>
      <div className="text-[13px] font-medium leading-[1.45] text-slate-900 break-words">
        {content}
      </div>
    </div>
  );
};

const InfoGrid = ({ children }) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-2">{children}</div>
);

const SummaryBadge = ({ label, value, icon = null }) => (
  <div className="rounded-2xl border border-white/60 bg-white/75 px-3 py-3 shadow-sm backdrop-blur-sm">
    <div className="flex items-center gap-2">
      {icon ? <span className="text-slate-500">{icon}</span> : null}
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">
          {label}
        </div>
        <div className="mt-1 truncate text-[13px] font-semibold text-slate-900">
          {safeText(value)}
        </div>
      </div>
    </div>
  </div>
);

function safeParseFamilyHistory(v) {
  try {
    if (!v) return { summary: "", relations: [] };
    if (typeof v === "object") {
      return {
        summary: typeof v.summary === "string" ? v.summary : "",
        relations: Array.isArray(v.relations) ? v.relations : [],
      };
    }
    const obj = JSON.parse(String(v));
    return {
      summary: typeof obj?.summary === "string" ? obj.summary : "",
      relations: Array.isArray(obj?.relations) ? obj.relations : [],
    };
  } catch {
    return { summary: "", relations: [] };
  }
}

function hasMeaningfulFamilyHistory(rec) {
  if (!rec) return false;
  const fh = rec.family_history;
  if (fh === null || fh === undefined) return false;

  if (typeof fh === "object") {
    const relations = Array.isArray(fh.relations) ? fh.relations : fh.relations == null ? [] : null;
    const summary =
      typeof fh.summary === "string"
        ? fh.summary.trim()
        : fh.summary == null
        ? ""
        : String(fh.summary).trim();

    if (Array.isArray(relations) && relations.length === 0 && summary === "") return false;
    if ((Array.isArray(relations) && relations.length > 0) || summary !== "") return true;
    return false;
  }

  if (typeof fh === "string") {
    const trimmed = fh.trim();
    if (trimmed === "") return false;
    const lower = trimmed.toLowerCase();
    if (lower === "na") return false;
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        const rels = Array.isArray(parsed?.relations) ? parsed.relations : [];
        const summ = (parsed?.summary ?? "").toString().trim();
        if (rels.length === 0 && summ === "") return false;
        return true;
      } catch (e) {}
    }
    return true;
  }

  return false;
}

const EmptyState = ({ text }) => (
  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-[13px] text-slate-600">
    {text}
  </div>
);

const LinkText = ({ href, children }) =>
  href ? (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 text-[13px] font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
    >
      {children}
      <FiExternalLink size={12} />
    </a>
  ) : (
    "-"
  );

const GridHeader = ({ displayName }) => (
  <div className="flex h-full w-full items-center px-4 text-[12px] font-medium text-slate-800">
    <span className="truncate">{displayName}</span>
  </div>
);

const GridCell = ({ value }) => (
  <div className="flex h-full w-full items-center px-4 text-[13px] text-slate-900">
    <span className="truncate">{safeText(value)}</span>
  </div>
);

export default function AcrisLegacyLeadDetail() {
  const { id } = useParams();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fhModalOpen, setFhModalOpen] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${BASE_URL}/api/acris-legacy/leads/${id}`);
      setRow(res.data || null);
    } catch (err) {
      console.error("AcrisLegacy detail error", err);
      setError("Failed to load lead detail.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const partiesColumns = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        minWidth: 190,
        flex: 1.15,
        headerComponent: GridHeader,
        cellRenderer: (params) => <GridCell value={params.value} />,
      },
      {
        headerName: "Address",
        field: "address",
        minWidth: 230,
        flex: 1.35,
        headerComponent: GridHeader,
        cellRenderer: (params) => <GridCell value={params.value} />,
      },
      {
        headerName: "Role",
        field: "role",
        width: 130,
        minWidth: 120,
        headerComponent: GridHeader,
        cellRenderer: (params) => <GridCell value={params.value} />,
      },
    ],
    []
  );

  const crossRefColumns = useMemo(
    () => [
      {
        headerName: "CRFN",
        field: "crfn",
        minWidth: 170,
        flex: 1.2,
        headerComponent: GridHeader,
        cellRenderer: (params) => <GridCell value={params.value} />,
      },
      {
        headerName: "Borough",
        field: "borough",
        width: 120,
        minWidth: 110,
        headerComponent: GridHeader,
        cellRenderer: (params) => <GridCell value={params.value} />,
      },
      {
        headerName: "Year",
        field: "year",
        width: 95,
        minWidth: 90,
        headerComponent: GridHeader,
        cellRenderer: (params) => <GridCell value={params.value} />,
      },
      {
        headerName: "Reel",
        field: "reel",
        width: 95,
        minWidth: 90,
        headerComponent: GridHeader,
        cellRenderer: (params) => <GridCell value={params.value} />,
      },
      {
        headerName: "Page",
        field: "page",
        width: 95,
        minWidth: 90,
        headerComponent: GridHeader,
        cellRenderer: (params) => <GridCell value={params.value} />,
      },
    ],
    []
  );

  const pdfInfo = buildPdfLink(row);
  const fhParsed = safeParseFamilyHistory(row?.family_history);
  const fhSummaryParsed = (fhParsed?.summary ?? "").toString();
  const fhRelationsParsed = Array.isArray(fhParsed?.relations) ? fhParsed.relations : [];

  const partiesHeight = useMemo(() => getGridHeight(row?.det_parties || [], 42, 42, 180, 250), [row]);
  const crossRefHeight = useMemo(
    () => getGridHeight(row?.det_cross_refs || [], 42, 42, 180, 250),
    [row]
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-[1800px] p-3 md:p-4 lg:p-5">
        <div className="space-y-4">
          <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-r from-indigo-50 via-white to-cyan-50 shadow-[0_18px_40px_rgba(15,23,42,0.06)]">
            <div
              className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.12),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(6,182,212,0.10),transparent_28%)]"
              aria-hidden
            />
            <div className="relative px-4 py-4 md:px-5 md:py-5 lg:px-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    ACRIS Legacy
                  </p>
                  <h1 className="mt-1 text-[24px] font-bold leading-tight text-slate-900">
                    Lead Detail
                  </h1>
                  <p className="mt-1 text-[13px] text-slate-600">Record ID: {id}</p>
                </div>

                <Link
                  className="inline-flex h-10 items-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 text-[13px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                  to="/acris-legacy/leads"
                >
                  <FiArrowLeft size={14} />
                  Back to leads
                </Link>
              </div>

              {row && (
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  <SummaryBadge label="Full Name" value={row.full_name || "—"} icon={<FiUsers size={14} />} />
                  <SummaryBadge
                    label="Document ID"
                    value={row.acris_document_id || "—"}
                    icon={<FiFileText size={14} />}
                  />
                  <SummaryBadge
                    label="Match Confidence"
                    value={row.match_confidence || "—"}
                    icon={<FiCheckCircle size={14} />}
                  />
                  <SummaryBadge
                    label="Latest Owner"
                    value={row.latest_owner || "—"}
                    icon={<FiHome size={14} />}
                  />
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700 shadow-sm">
              {error}
            </div>
          )}

          {loading && (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[13px] text-slate-700 shadow-sm">
              Loading...
            </div>
          )}

          {row && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <SectionCard
                  title="Deceased Info"
                  subtitle="Obituary and identity details"
                  icon={<FiFileText size={15} />}
                >
                  <InfoGrid>
                    <Field label="Full name" value={row.full_name} />
                    <Field label="First name" value={row.first_name} />
                    <Field label="Last name" value={row.last_name} />
                    <Field label="Date of death" value={formatDate(row.obituary_date_of_death)} />
                    <Field label="Published date" value={formatDate(row.obituary_published_date)} />
                    <Field
                      label="Obituary URL"
                      value={<LinkText href={row.obituary_url}>Open obituary</LinkText>}
                    />
                  </InfoGrid>
                </SectionCard>

                <SectionCard
                  title="Property / BBL"
                  subtitle="Property location and BBL information"
                  icon={<FiMapPin size={15} />}
                >
                  <InfoGrid>
                    <Field label="Full address" value={row.property_full_address} className="sm:col-span-2" />
                    <Field label="Borough" value={row.property_borough_name} />
                    <Field label="Borough code" value={row.property_borough} />
                    <Field label="Block" value={row.property_block} />
                    <Field label="Lot" value={row.property_lot} />
                    <Field label="House number" value={row.property_house_number} />
                    <Field label="Street name" value={row.property_street_name} />
                    <Field label="Unit" value={row.property_unit} />
                  </InfoGrid>
                </SectionCard>
              </div>

              <SectionCard
                title="Matched ACRIS Document"
                subtitle="Matched deed and document metadata"
                icon={<FiFileText size={15} />}
              >
                <InfoGrid>
                  <Field label="Document ID" value={row.acris_document_id} />
                  <Field label="Doc type" value={row.acris_doc_type} />
                  <Field label="Doc date" value={formatDate(row.acris_document_date)} />
                  <Field label="Doc amount" value={formatMoney(row.acris_doc_amount)} />
                  <Field label="Recording date" value={safeText(row.acris_recording_date)} />
                  <Field label="Match confidence" value={row.match_confidence} />
                  <Field label="Matched party" value={row.matched_party_name} />
                  <Field label="Matched role" value={row.matched_party_role} />
                </InfoGrid>
              </SectionCard>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <SectionCard
                  title="Ownership"
                  subtitle="Current and historical ownership details"
                  icon={<FiUsers size={15} />}
                >
                  <InfoGrid>
                    <Field label="Is latest owner" value={row.is_latest_owner === 1 ? "Yes" : "No"} />
                    <Field label="Latest owner" value={row.latest_owner} />
                    <Field label="Grantee names" value={row.grantee_names} />
                    <Field label="Grantor names" value={row.grantor_names} />
                    <Field label="Owner 1" value={row.owner1} />
                    <Field label="Owner 2" value={row.owner2} />
                    <Field label="Owner 3" value={row.owner3} />
                    <Field label="Owner 4" value={row.owner4} />
                    <Field label="Owner 5" value={row.owner5} />
                  </InfoGrid>
                </SectionCard>

                <SectionCard
                  title="Latest Deed on Property"
                  subtitle="Most recent deed summary"
                  icon={<FiHome size={15} />}
                >
                  <InfoGrid>
                    <Field label="Latest deed date" value={formatDate(row.latest_deed_date)} />
                    <Field label="Latest deed type" value={row.latest_deed_type} />
                    <Field label="Latest deed grantor" value={row.latest_deed_grantor} />
                    <Field label="Latest deed grantee" value={row.latest_deed_grantee} />
                  </InfoGrid>
                </SectionCard>
              </div>

              <SectionCard
                title="PDF / External Links"
                subtitle="Open source documents and external pages"
                icon={<FiExternalLink size={15} />}
              >
                <InfoGrid>
                  <Field label="PDF status" value={pdfInfo.label} />
                  <Field label="Local PDF" value={<LinkText href={pdfInfo.href}>Open PDF</LinkText>} />
                  <Field
                    label="ACRIS Image"
                    value={<LinkText href={row.deed_pdf_url}>View on ACRIS</LinkText>}
                  />
                  <Field
                    label="ACRIS Detail"
                    value={<LinkText href={row.deed_det_url}>Detail page</LinkText>}
                  />
                </InfoGrid>
              </SectionCard>

              <SectionCard
                title="Family History (Manual)"
                subtitle="Separate from ACRIS parties"
                icon={<FiUsers size={15} />}
              >
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div className="text-[12px] text-slate-500">
                    Manage manually added family members, phones, emails, and addresses.
                  </div>
                  <button
                    onClick={() => setFhModalOpen(true)}
                    className="inline-flex h-10 items-center rounded-xl border border-slate-200 bg-white px-4 text-[13px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                    title="Edit Family History"
                  >
                    Edit
                  </button>
                </div>

                {hasMeaningfulFamilyHistory(row) ? (
                  <div className="space-y-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                        Summary
                      </div>
                      <div className="text-[13px] leading-[1.55] text-slate-800 whitespace-pre-line">
                        {fhSummaryParsed?.trim() ? fhSummaryParsed : "—"}
                      </div>
                    </div>

                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                      <div className="overflow-auto">
                        <table className="w-full min-w-[900px] text-[13px]">
                          <thead className="border-b border-slate-200 bg-slate-50">
                            <tr>
                              <th className="px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                                Name
                              </th>
                              <th className="px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                                Relationship
                              </th>
                              <th className="px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                                Phones
                              </th>
                              <th className="px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                                Addresses
                              </th>
                              <th className="px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                                Emails
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {fhRelationsParsed.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="px-3 py-4 text-[12px] text-slate-500">
                                  No relations added.
                                </td>
                              </tr>
                            ) : (
                              fhRelationsParsed.map((r, idx) => {
                                const phones = (Array.isArray(r?.phones) ? r.phones : r?.phone ? [r.phone] : [])
                                  .map((p) => onlyDigits(p))
                                  .filter(Boolean);

                                const emails = (Array.isArray(r?.emails) ? r.emails : r?.email ? [r.email] : [])
                                  .map((e) => toStr(e).trim())
                                  .filter(Boolean);

                                const addresses = (
                                  Array.isArray(r?.addresses) ? r.addresses : r?.address ? [r.address] : []
                                )
                                  .map((a) => toStr(a).trim())
                                  .filter(Boolean);

                                return (
                                  <tr key={idx} className="border-t border-slate-100 align-top">
                                    <td className="px-3 py-3 font-medium text-slate-900">
                                      {(r?.name ?? "—") || "—"}
                                    </td>
                                    <td className="px-3 py-3 text-slate-700">
                                      {(r?.relationship ?? "—") || "—"}
                                    </td>
                                    <td className="px-3 py-3 text-slate-700">{phones.join(", ") || "—"}</td>
                                    <td className="px-3 py-3 text-slate-700">{addresses.join(", ") || "—"}</td>
                                    <td className="px-3 py-3 text-slate-700">{emails.join(", ") || "—"}</td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <EmptyState text="No family history yet. Click Edit to add members and contacts." />
                )}
              </SectionCard>

              <SectionCard
                title="Status & Notes"
                subtitle="Track progress and internal notes"
                icon={<FiShield size={15} />}
              >
                <StatusNotesPanel entityId={id} apiBase="/api/acris-legacy/leads" />
              </SectionCard>

              <SectionCard
                title="Verification / Metadata"
                subtitle="System verification and timestamps"
                icon={<FiClock size={15} />}
              >
                <InfoGrid>
                  <Field label="ACRIS verified" value={row.acris_verified ? "Yes" : "No"} />
                  <Field label="Verified at" value={row.acris_verified_at} />
                  <Field label="Scrape status" value={row.acris_scrape_status} />
                  <Field label="Created at" value={row.created_at} />
                  <Field label="Updated at" value={row.updated_at} />
                </InfoGrid>
              </SectionCard>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <GridCard
                  title="Parties"
                  subtitle="Parties attached to the matched document"
                  icon={<FiUsers size={15} />}
                >
                  <AppDataGrid
                    rowData={row.det_parties || []}
                    columnDefs={partiesColumns}
                    height={partiesHeight}
                    minWidth={0}
                    rowHeight={42}
                    headerHeight={42}
                    overlayNoRowsTemplate={
                      '<span class="ag-overlay-loading-center">No party records found.</span>'
                    }
                    defaultColDef={{
                      resizable: true,
                      sortable: false,
                      filter: false,
                      suppressMenu: true,
                      cellStyle: {
                        padding: 0,
                      },
                    }}
                  />
                </GridCard>

                <GridCard
                  title="Cross References"
                  subtitle="Related ACRIS cross-reference records"
                  icon={<FiFileText size={15} />}
                >
                  <AppDataGrid
                    rowData={row.det_cross_refs || []}
                    columnDefs={crossRefColumns}
                    height={crossRefHeight}
                    minWidth={0}
                    rowHeight={42}
                    headerHeight={42}
                    overlayNoRowsTemplate={
                      '<span class="ag-overlay-loading-center">No cross-reference records found.</span>'
                    }
                    defaultColDef={{
                      resizable: true,
                      sortable: false,
                      filter: false,
                      suppressMenu: true,
                      cellStyle: {
                        padding: 0,
                      },
                    }}
                  />
                </GridCard>
              </div>
            </div>
          )}

          {!loading && !error && !row && (
            <EmptyState text="No lead detail found for this record." />
          )}
        </div>
      </div>

      <FamilyHistoryModal
        open={fhModalOpen}
        record={row}
        onClose={() => setFhModalOpen(false)}
        onSaved={fetchDetail}
        apiBase="/api/acris-legacy/leads"
      />
    </div>
  );
}